/**
 * 1.6.0 the agent API gateway ("site services", plan Part B; docs/dev/mcp-1.6.0-build.md). Shared by the storage agent
 * (stream G: it loads gateway.json, reports it, and runs `http` jobs) and PRISM (stream W: the capability check, the
 * Call site service step and the Webhooks page section).
 *
 *  - the allowlist file `gateway.json` (GatewayFileSchema): the site's services, each with its methods, path patterns,
 *    secret headers (`env:NAME`, `file:path` or a literal), limits and TLS options. The agent's file is the authority;
 *    PRISM never edits it and never sees a header value or name;
 *  - path matching (normaliseServicePath, pathMatches, matchServiceRequest): both sides check a request with the same
 *    code, the agent again before it sends anything;
 *  - what the agent reports in its heartbeat (GatewayCapabilitySchema: origins only, no headers);
 *  - the `http` job's result (HttpResultSchema) and its failure codes (SITE_SERVICE_ERRORS); the job spec is
 *    AgentHttpJob in agent.ts.
 *
 * Pure and dependency-free apart from zod: the agent bundles it.
 */
import { z } from 'zod';

/* ---------------------------------------------------------------- limits */

export const GATEWAY_METHODS = ['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type GatewayMethod = (typeof GATEWAY_METHODS)[number];

export const GATEWAY_LIMITS = {
  services: 50,
  pathsPerService: 100,
  pathLength: 2000,
  timeoutSeconds: { min: 1, max: 120, default: 20 },
  maxResponseKB: { min: 1, max: 4096, default: 256 },
  /** A request body (JSON or text) is at most this many bytes (UTF-8). */
  maxRequestBytes: 1024 * 1024,
  /** Request headers PRISM may pass (by name, each service's allowRequestHeaders). */
  requestHeaders: 20,
  /** The gateway lane's concurrency (PRISM_AGENT_GATEWAY_CONCURRENCY). */
  concurrency: { min: 1, max: 32, default: 4 },
} as const;

/** The response headers an `http` job returns; every other header stays on the agent. */
export const GATEWAY_RESPONSE_HEADERS = ['content-type', 'content-length', 'etag', 'last-modified', 'location', 'retry-after', 'x-request-id'] as const;

/** Header names PRISM may never pass or set, whatever a service allows. */
export const GATEWAY_FORBIDDEN_HEADERS = new Set(['authorization', 'proxy-authorization', 'cookie', 'host', 'content-length', 'transfer-encoding', 'connection', 'upgrade', 'te', 'trailer', 'keep-alive', 'x-forwarded-for', 'x-forwarded-host', 'x-forwarded-proto', 'forwarded']);

/* ---------------------------------------------------------------- paths */

/**
 * Characters a decoded path segment may contain without re-encoding (RFC 3986 pchar minus `%`): the canonical path
 * re-encodes everything else.
 */
const SEGMENT_SAFE = /[A-Za-z0-9\-._~!$&'()*+,;=:@]/;

function encodeSegment(s: string): string {
  let out = '';
  for (const ch of s) out += SEGMENT_SAFE.test(ch) ? ch : encodeURIComponent(ch);
  return out;
}

export type NormalisedPath = { ok: true; path: string; segments: string[]; trailingSlash: boolean } | { ok: false; reason: string };

/**
 * A request path as both sides check and send it. Refused: anything not starting with `/`, a query or fragment (`?`,
 * `#`: the query travels separately), a backslash, control characters or NUL (raw or encoded), invalid or encoded
 * slashes and backslashes (`%2F`, `%5C`), and `.` or `..` segments, raw or encoded (`%2e%2e`); paths longer than 2000
 * characters. Repeated slashes are collapsed. The answer's `path` is the canonical form (each decoded segment
 * re-encoded), which is what the agent sends; `segments` are the decoded segments the patterns are matched against.
 */
export function normaliseServicePath(raw: string): NormalisedPath {
  if (typeof raw !== 'string' || !raw.startsWith('/')) return { ok: false, reason: 'The path must start with /' };
  if (raw.length > GATEWAY_LIMITS.pathLength) return { ok: false, reason: `The path is longer than ${GATEWAY_LIMITS.pathLength} characters` };
  if (/[?#]/.test(raw)) return { ok: false, reason: 'The path may not contain ? or # (send the query separately)' };
  if (raw.includes('\\')) return { ok: false, reason: 'The path may not contain a backslash' };
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\s]/.test(raw)) return { ok: false, reason: 'The path may not contain spaces or control characters (encode them)' };
  const parts = raw.split('/').slice(1);
  const trailingSlash = parts.length > 1 && parts[parts.length - 1] === '';
  const segments: string[] = [];
  for (const part of parts) {
    if (part === '') continue; // repeated or trailing slashes
    if (/%(?![0-9A-Fa-f]{2})/.test(part)) return { ok: false, reason: 'The path has an invalid percent-encoding' };
    if (/%2f|%5c/i.test(part)) return { ok: false, reason: 'Encoded slashes are not allowed in the path' };
    let decoded: string;
    try {
      decoded = decodeURIComponent(part);
    } catch {
      return { ok: false, reason: 'The path has an invalid percent-encoding' };
    }
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(decoded)) return { ok: false, reason: 'The path may not contain control characters' };
    if (decoded === '.' || decoded === '..') return { ok: false, reason: 'The path may not contain . or .. segments' };
    segments.push(decoded);
  }
  const path = '/' + segments.map(encodeSegment).join('/') + (trailingSlash && segments.length ? '/' : '');
  return { ok: true, path, segments, trailingSlash: trailingSlash && segments.length > 0 };
}

/** Why a path pattern of gateway.json is not valid; null when it is. */
export function pathPatternProblem(pattern: string): string | null {
  if (!pattern.startsWith('/')) return 'A path pattern must start with /';
  if (pattern.length > 500) return 'A path pattern is at most 500 characters';
  const segs = pattern.split('/').slice(1).filter((s, i, all) => s !== '' || i < all.length - 1);
  for (const s of segs) {
    if (s === '') return 'A path pattern may not contain //';
    if (s === '**') return '** is not supported: * as the last segment matches the rest of the path';
    if (s.includes('*') && s !== '*') return '* matches a whole segment only (e.g. /stories/*, not /stories/abc*)';
    if (s === '.' || s === '..') return 'A path pattern may not contain . or .. segments';
    if (/[?#\\%]/.test(s)) return 'A path pattern may not contain ?, #, \\ or percent-encoding (write the decoded segment)';
  }
  return null;
}

/**
 * Whether decoded path segments match a pattern of gateway.json:
 *  - a literal segment matches itself exactly (case-sensitive);
 *  - `*` in the middle matches exactly one segment;
 *  - `*` as the last segment matches one or more segments (the rest of the path);
 *  - a pattern without a trailing `*` is a prefix on segment boundaries: `/stories` matches `/stories` and
 *    `/stories/1/text`, never `/storiesx`; `/` matches every path.
 */
export function pathMatches(pattern: string, segments: readonly string[]): boolean {
  if (pathPatternProblem(pattern)) return false;
  const pat = pattern.split('/').slice(1).filter(Boolean);
  for (let i = 0; i < pat.length; i++) {
    const p = pat[i]!;
    const last = i === pat.length - 1;
    if (p === '*') {
      if (last) return segments.length > i;
      if (segments[i] === undefined) return false;
      continue;
    }
    if (segments[i] !== p) return false;
  }
  return true;
}

/* ---------------------------------------------------------------- gateway.json */

const SERVICE_NAME = /^[a-z][a-z0-9-]{0,39}$/;
const HEADER_NAME = /^[A-Za-z0-9!#$%&'*+.^_`|~-]{1,100}$/;

/** Where a header's value comes from: the agent host's environment, a file, or the literal text (warned about). */
export type HeaderValueSource = { kind: 'env'; name: string } | { kind: 'file'; path: string } | { kind: 'literal'; value: string };
export function headerValueSource(v: string): HeaderValueSource {
  if (v.startsWith('env:')) return { kind: 'env', name: v.slice(4) };
  if (v.startsWith('file:')) return { kind: 'file', path: v.slice(5) };
  return { kind: 'literal', value: v };
}

const BaseUrl = z.string().max(500).superRefine((v, ctx) => {
  let u: URL;
  try { u = new URL(v); } catch { ctx.addIssue({ code: 'custom', message: 'baseUrl must be an absolute http:// or https:// address' }); return; }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') ctx.addIssue({ code: 'custom', message: 'baseUrl must use http or https' });
  if (u.username || u.password) ctx.addIssue({ code: 'custom', message: 'baseUrl may not contain credentials; use a header (env: or file:)' });
  if (u.search || u.hash) ctx.addIssue({ code: 'custom', message: 'baseUrl may not contain a query or fragment' });
});

const PathPattern = z.string().superRefine((v, ctx) => {
  const problem = pathPatternProblem(v);
  if (problem) ctx.addIssue({ code: 'custom', message: problem });
});

export const GatewayServiceSchema = z.object({
  /** Lowercase, unique in the file: what a flow names. */
  name: z.string().regex(SERVICE_NAME, 'name: lowercase letters, digits and -, starting with a letter (at most 40)'),
  baseUrl: BaseUrl,
  methods: z.array(z.enum(GATEWAY_METHODS)).min(1).max(GATEWAY_METHODS.length).refine((m) => new Set(m).size === m.length, 'methods are listed once each'),
  paths: z.array(PathPattern).min(1).max(GATEWAY_LIMITS.pathsPerService),
  /** Secret headers the agent adds: `env:NAME`, `file:path` (relative to gateway.json) or a literal (warned). */
  headers: z.record(z.string().regex(HEADER_NAME), z.string().min(1).max(4096)).default({}),
  /** Request header names PRISM may pass with a call (non-secret: e.g. Accept-Language). */
  allowRequestHeaders: z.array(z.string().regex(HEADER_NAME)).max(GATEWAY_LIMITS.requestHeaders).default([])
    .refine((h) => h.every((n) => !GATEWAY_FORBIDDEN_HEADERS.has(n.toLowerCase())), 'allowRequestHeaders may not list Authorization, Cookie, Host or hop-by-hop headers'),
  timeoutSeconds: z.number().int().min(GATEWAY_LIMITS.timeoutSeconds.min).max(GATEWAY_LIMITS.timeoutSeconds.max).default(GATEWAY_LIMITS.timeoutSeconds.default),
  maxResponseKB: z.number().int().min(GATEWAY_LIMITS.maxResponseKB.min).max(GATEWAY_LIMITS.maxResponseKB.max).default(GATEWAY_LIMITS.maxResponseKB.default),
  /** What `gateway check` and PRISM's Test request (HEAD, else GET); default `/`. Must match the paths too. */
  healthPath: z.string().max(GATEWAY_LIMITS.pathLength).optional(),
  tls: z.object({
    /** A CA certificate (PEM) to trust for this service, relative to gateway.json. */
    caFile: z.string().min(1).max(1000).optional(),
    /** Skips certificate checks (warned by `check` and in the log). */
    insecureSkipVerify: z.boolean().optional(),
  }).strict().optional(),
  description: z.string().max(300).default(''),
}).strict();
export type GatewayService = z.infer<typeof GatewayServiceSchema>;

export const GatewayFileSchema = z.object({
  services: z.array(GatewayServiceSchema).max(GATEWAY_LIMITS.services),
}).strict().superRefine((f, ctx) => {
  const seen = new Set<string>();
  f.services.forEach((s, i) => {
    if (seen.has(s.name)) ctx.addIssue({ code: 'custom', path: ['services', i, 'name'], message: `The service name ${s.name} is used twice` });
    seen.add(s.name);
    if (s.healthPath !== undefined) {
      const n = normaliseServicePath(s.healthPath);
      if (!n.ok) ctx.addIssue({ code: 'custom', path: ['services', i, 'healthPath'], message: n.reason });
      else if (!s.paths.some((p) => pathMatches(p, n.segments))) ctx.addIssue({ code: 'custom', path: ['services', i, 'healthPath'], message: 'healthPath must match one of the paths' });
    }
  });
});
export type GatewayFile = z.infer<typeof GatewayFileSchema>;

/** Warnings `gateway check` prints for a valid file (literal secrets, skipped TLS checks, plain http, `/` patterns). */
export function gatewayFileWarnings(file: GatewayFile): string[] {
  const out: string[] = [];
  for (const s of file.services) {
    for (const [h, v] of Object.entries(s.headers)) if (headerValueSource(v).kind === 'literal') out.push(`${s.name}: the header ${h} is written in the file; prefer env:NAME or file:path`);
    if (s.tls?.insecureSkipVerify) out.push(`${s.name}: insecureSkipVerify is on, so the certificate is not checked`);
    if (s.baseUrl.startsWith('http:')) out.push(`${s.name}: baseUrl uses plain http`);
    if (s.paths.some((p) => p.split('/').filter(Boolean).length === 0)) out.push(`${s.name}: the path pattern / allows every path`);
  }
  return out;
}

/* ---------------------------------------------------------------- checking a request */

export type ServiceRequestVerdict =
  | { ok: true; path: string; segments: string[] }
  | { ok: false; code: 'UNKNOWN_SITE_SERVICE' | 'SITE_SERVICE_METHOD' | 'SITE_SERVICE_PATH'; reason: string };

/** What both sides check before a call: the service is listed, the method allowed, the normalised path matched. */
export function matchServiceRequest(
  services: ReadonlyArray<Pick<GatewayService, 'name' | 'methods' | 'paths'>>, name: string, method: string, rawPath: string,
): ServiceRequestVerdict {
  const s = services.find((x) => x.name === name);
  if (!s) return { ok: false, code: 'UNKNOWN_SITE_SERVICE', reason: `The agent has no site service called ${name}` };
  const m = method.toUpperCase();
  if (!(s.methods as readonly string[]).includes(m)) return { ok: false, code: 'SITE_SERVICE_METHOD', reason: `${name} does not allow ${m} (allowed: ${s.methods.join(', ')})` };
  const n = normaliseServicePath(rawPath);
  if (!n.ok) return { ok: false, code: 'SITE_SERVICE_PATH', reason: n.reason };
  if (!s.paths.some((p) => pathMatches(p, n.segments))) return { ok: false, code: 'SITE_SERVICE_PATH', reason: `${name} does not allow the path ${n.path} (allowed: ${s.paths.join(', ')})` };
  return { ok: true, path: n.path, segments: n.segments };
}

/** The URL the agent calls: the base URL's path joined with the canonical path, and the query. */
export function joinServiceUrl(baseUrl: string, canonicalPath: string, query?: Record<string, string | string[]>): string {
  const u = new URL(baseUrl);
  const base = u.pathname.replace(/\/+$/, '');
  u.pathname = `${base}${canonicalPath}`;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) for (const one of [v].flat()) params.append(k, one);
  u.search = params.toString();
  return u.toString();
}

/* ---------------------------------------------------------------- the heartbeat report */

export const GatewayServiceReportSchema = z.object({
  name: z.string().regex(SERVICE_NAME),
  /** Scheme, host and port only (the base URL's origin); never its path, never a header. */
  origin: z.string().max(300),
  methods: z.array(z.enum(GATEWAY_METHODS)),
  paths: z.array(z.string().max(500)).max(GATEWAY_LIMITS.pathsPerService),
  description: z.string().max(300).default(''),
  timeoutSeconds: z.number().int(),
  healthPath: z.string().max(GATEWAY_LIMITS.pathLength).optional(),
});
export type GatewayServiceReport = z.infer<typeof GatewayServiceReportSchema>;

/**
 * capabilities.gateway in the heartbeat (agents from 1.6.0 with the gateway on): the services of a valid file, or
 * none and why the file was refused. `operations` lists 'gateway' only with a valid file.
 */
export const GatewayCapabilitySchema = z.object({
  services: z.array(GatewayServiceReportSchema).max(GATEWAY_LIMITS.services).default([]),
  /** The file was refused as a whole: the first problems, with their line numbers. */
  fileError: z.string().max(2000).optional(),
  concurrency: z.number().int().positive().optional(),
});
export type GatewayCapability = z.infer<typeof GatewayCapabilitySchema>;

/** One service of the file as the agent reports it. */
export function gatewayServiceReport(s: GatewayService): GatewayServiceReport {
  return {
    name: s.name, origin: new URL(s.baseUrl).origin, methods: [...s.methods], paths: [...s.paths], description: s.description,
    timeoutSeconds: s.timeoutSeconds, ...(s.healthPath ? { healthPath: s.healthPath } : {}),
  };
}

/** The agent operation that says the gateway is on with a valid file. */
export const GATEWAY_OPERATION = 'gateway';

/** What an agent's gateway looks like from PRISM (Webhooks → Site services): from its operations and report. */
export type AgentGatewayState = 'off' | 'on' | 'file_error' | 'too_old';

/* ---------------------------------------------------------------- the http job's result */

export const HttpResultSchema = z.object({
  /** The service's HTTP status (a 4xx or 5xx is still a succeeded job: the step maps it). */
  status: z.number().int().min(100).max(599),
  /** Only GATEWAY_RESPONSE_HEADERS, lowercase names. */
  headers: z.record(z.string(), z.string().max(2000)).default({}),
  bodyType: z.enum(['json', 'text', 'empty']),
  /** A JSON body parsed, else text (up to the service's maxResponseKB). */
  body: z.unknown(),
  durationMs: z.number().int().nonnegative(),
  /** The answer was longer than maxResponseKB: the body is cut (text) or absent (JSON that could not be parsed). */
  truncated: z.boolean(),
});
export type HttpResult = z.infer<typeof HttpResultSchema>;

/** Failure codes of an `http` job (JobCompletion error.code), with whether the step retries them. */
export const SITE_SERVICE_ERRORS = {
  GATEWAY_OFF: { retryable: false, message: 'The agent’s gateway is off' },
  GATEWAY_FILE_ERROR: { retryable: false, message: 'The agent’s gateway.json was refused' },
  UNKNOWN_SITE_SERVICE: { retryable: false, message: 'The agent has no such site service' },
  SITE_SERVICE_METHOD: { retryable: false, message: 'The site service does not allow this method' },
  SITE_SERVICE_PATH: { retryable: false, message: 'The site service does not allow this path' },
  SITE_SERVICE_HEADER: { retryable: false, message: 'The site service does not allow this request header' },
  SITE_SERVICE_BODY: { retryable: false, message: 'The request body is too large or not valid' },
  SITE_SERVICE_SECRET: { retryable: false, message: 'A header’s secret could not be read on the agent' },
  SITE_SERVICE_TIMEOUT: { retryable: true, message: 'The site service did not answer in time' },
  SITE_SERVICE_UNREACHABLE: { retryable: true, message: 'The site service could not be reached' },
  SITE_SERVICE_TLS: { retryable: false, message: 'The site service’s certificate was not accepted' },
  SITE_SERVICE_REDIRECT: { retryable: false, message: 'The site service answered with a redirect, which the agent does not follow' },
} as const;
export type SiteServiceErrorCode = keyof typeof SITE_SERVICE_ERRORS;

/** How Call site service treats a service's status: 2xx ok, 429 and 5xx retried, other 4xx failed without retry. */
export function siteServiceOutcome(status: number): 'ok' | 'retry' | 'fail' {
  if (status >= 200 && status < 300) return 'ok';
  if (status === 429 || status >= 500) return 'retry';
  return 'fail';
}

/* ---------------------------------------------------------------- PRISM's view (Webhooks → Site services) */

export interface SiteServiceDTO extends GatewayServiceReport {
  /** Workflows whose published or draft steps call it. */
  usedBy: Array<{ id: string; name: string }>;
  /** The last call (an agent job of kind http): its status (null: failed without one) and when. */
  lastCall: { status: number | null; error: string | null; at: string } | null;
}

export interface AgentSiteServicesDTO {
  agentId: string;
  agentName: string;
  site: string;
  connected: boolean;
  state: AgentGatewayState;
  fileError: string | null;
  services: SiteServiceDTO[];
}

/** POST /api/v1/site-services/:agentId/:service/test: one HEAD or GET of the health path, through the agent (≤ 25 s). */
export interface SiteServiceTestResult {
  ok: boolean;
  method: 'HEAD' | 'GET';
  path: string;
  status: number | null;
  durationMs: number | null;
  error: { code: string; message: string } | null;
}

/** The synchronous Test waits at most this long for the agent (the folder picker's pattern). */
export const SITE_SERVICE_TEST_DEADLINE_MS = 25_000;
